import React, { useState } from "react";
import "./Parser.css";

const Parser = ({ dsName = "", data }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [viewMode, setViewMode] = useState("formatted"); // 'formatted' or 'raw'

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const formatKey = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatValue = (key, value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return value.toString();
  };

  const decodeEscapeSequences = (str) => {
    return str
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t")
      .replace(/\\r/g, "\r")
      .replace(/\\b/g, "\b")
      .replace(/\\f/g, "\f")
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\\\/g, "\\");
  };

  const processUnicodeEscape = (str) => {
    return str.replace(/\\u([\dA-Fa-f]{4})/g, (match, grp) => {
      return String.fromCharCode(parseInt(grp, 16));
    });
  };

  const renderInputData = (value) => {
    let decodedValue = decodeEscapeSequences(value);
    decodedValue = processUnicodeEscape(decodedValue);

    // Clean all ANSI/device sequences
    decodedValue = decodedValue.replace(/\u001b\[[0-9;?]*[A-Za-z]/g, "");
    decodedValue = decodedValue.replace(/\u001b\(.\d*/g, ""); // handles ESC(1 etc.

    return <div className="input-data-container">{decodedValue}</div>;
  };

  const getRawDataPreview = (value) => {
    let decodedValue = decodeEscapeSequences(value);
    decodedValue = processUnicodeEscape(decodedValue);

    // Clean all ANSI/device sequences
    decodedValue = decodedValue.replace(/\u001b\[[0-9;?]*[A-Za-z]/g, "");
    decodedValue = decodedValue.replace(/\u001b\(.\d*/g, "");

    const lines = decodedValue.split("\n");
    const previewLines = lines.slice(0, 3);

    return (
      <div className="input-data-preview-content">
        {previewLines.map((line, index) => (
          <div key={index} className="input-data-line preview-line">
            {line.substring(0, 100)}
            {line.length > 100 ? "..." : ""}
          </div>
        ))}
        {lines.length > 3 && (
          <div className="text-center mt-2 more-lines-indicator">
            ... and {lines.length - 3} more lines
          </div>
        )}
      </div>
    );
  };

  const renderFormattedTransaction = (item, index = null) => {
    let parsedData = {};

    try {
      parsedData =
        typeof item.data === "string" ? JSON.parse(item.data) : item.data;
    } catch (e) {
      return (
        <div key={index} className="transaction-card error">
          <div className="error-message">Invalid JSON data</div>
          <div className="raw-data">{item.data}</div>
        </div>
      );
    }

    return (
      <div key={index} className="transaction-card card-primary">
        <div className="transaction-header d-flex justify-content-between align-items-center background-primary">
          <h3 className="mb-0 color-dark">{dsName} Details</h3>
          <div className="d-flex align-items-center">
            <div className="transaction-id mr-3 color-light">
              ID: {item.id || "N/A"}
            </div>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() =>
                setViewMode(viewMode === "formatted" ? "raw" : "formatted")
              }
            >
              {viewMode === "formatted" ? "View Raw JSON" : "View Formatted"}
            </button>
          </div>
        </div>

        <div className="transaction-body background-primary">
          {Object.entries(parsedData).map(([key, value]) => {
            const isExpanded = expandedSections[key];
            const isInputData = key === "raw_data" || key === "input_data";

            return (
              <div
                key={key}
                className="transaction-field d-flex flex-wrap align-items-start border-bottom-light"
              >
                <div className="field-key col-md-5 p-2 font-weight-bold primary-color">
                  {formatKey(key)}:
                </div>
                <div
                  className={`field-value ${
                    isInputData ? "col-md-12" : "col-md-7"
                  } p-2`}
                >
                  {isInputData ? (
                    <>
                      <div
                        className={`field-header expandable d-flex justify-content-between align-items-center p-2 rounded ${
                          isExpanded
                            ? "expanded background-secondary"
                            : "collapsed background-secondary"
                        }`}
                        onClick={() => toggleSection(key)}
                      >
                        <span className="expand-text font-weight-medium color-dark">
                          {isExpanded
                            ? "Collapse Terminal Output"
                            : "Expand Terminal Output"}
                        </span>
                        <span className="expand-icon color-dark">
                          {isExpanded ? "▲" : "▼"}
                        </span>
                      </div>

                      {isExpanded ? (
                        <div className="input-data-expanded mt-2 background-secondary rounded">
                          {renderInputData(value)}
                        </div>
                      ) : (
                        <div className="input-data-preview mt-2 p-2 background-secondary rounded">
                          <div className="preview-header small text-muted mb-1">
                            Preview:
                          </div>
                          {getRawDataPreview(value)}
                          <div className="text-center mt-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => toggleSection(key)}
                            >
                              Click to view full raw data
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="color-light">
                      {formatValue(key, value)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="transaction-footer background-primary">
          <div className="metadata color-light">
            <span>Source File Name: {item.source_file_name || ""}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderRawTransaction = (item, index = null) => {
    return (
      <div
        key={index}
        className="transaction-card card-primary h-100 d-flex flex-column"
      >
        <div className="transaction-header d-flex justify-content-between align-items-center background-primary">
          <h3 className="mb-0 color-dark">{dsName} Details</h3>
          <div className="d-flex align-items-center">
            <div className="transaction-id mr-3 color-light">
              ID: {item.id || "N/A"}
            </div>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() =>
                setViewMode(viewMode === "formatted" ? "raw" : "formatted")
              }
            >
              {viewMode === "formatted" ? "View Raw JSON" : "View Formatted"}
            </button>
          </div>
        </div>

        <div className="transaction-body background-primary flex-grow-1 d-flex flex-column">
          <div className="raw-json-container background-secondary border rounded h-100 d-flex flex-column">
            <div className="raw-json-header p-2 border-bottom d-flex justify-content-between align-items-center">
              <span className="small text-muted">Raw JSON Data</span>
              <span className="small text-muted">
                {typeof item.data === "string" ? "String" : "Object"}
              </span>
            </div>
            <pre className="raw-json-content p-3 mb-0 flex-grow-1 overflow-auto">
              {typeof item.data === "string"
                ? JSON.stringify(JSON.parse(item.data), null, 2)
                : JSON.stringify(item.data, null, 2)}
            </pre>
          </div>
        </div>

        <div className="transaction-footer background-primary">
          <div className="metadata color-light">
            <span>Source File Name: {item.source_file_name || ""}</span>
          </div>
        </div>
      </div>
    );
  };

  if (!data)
    return (
      <div className="no-data p-4 text-center text-muted">No Record Found!</div>
    );

  try {
    const parsed = typeof data === "string" ? JSON?.parse(data) : data;

    if (Array.isArray(parsed)) {
      return (
        <div className="transactions-container">
          {parsed.map((item, index) =>
            viewMode === "formatted"
              ? renderFormattedTransaction(item, index)
              : renderRawTransaction(item, index)
          )}
        </div>
      );
    }

    if (parsed?.data) {
      return (
        <div className="transactions-container">
          {viewMode === "formatted"
            ? renderFormattedTransaction(parsed)
            : renderRawTransaction(parsed)}
        </div>
      );
    }

    return (
      <div className="error-message p-3 background-primary">
        Invalid data format!
      </div>
    );
  } catch (error) {
    return (
      <div className="error-message p-3 background-primary">
        Error parsing data!
      </div>
    );
  }
};

export default Parser;
